import React, { useContext, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Typography,
    Radio,
    RadioGroup,
    Button,
    Box,
    IconButton,
    BDSProvider
} from '@barracuda-internal/bds-core-v2';
import cudaLogo from 'src/static/barracuda_logo_text.svg';
import Close from '@mui/icons-material/Close';
import { FormControlLabel, TextField, styled } from '@material-ui/core';
import useStyles from 'src/styles/di-theme';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import { SideDetailContext } from './SideDetailPanel';
import useAuth from 'src/hooks/useAuth';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import { Alert } from '@material-ui/lab';
import useDetectionStyles from './styles/detection';

const feedbackOptions = [
    {
        label: 'Remove detection',
        value: 'RemoveDetection',
        helperText: '(Optional) Please explain why this detection should be removed (e.g., irrelevant, incorrect)'
    },
    {
        label: 'Keep detection, but requires review',
        value: 'KeepDetection',
        helperText: 'Please provide details about the misclassification or label that needs correction.'
    }
];

const StyledTextField = styled(TextField)({
    '&.MuiFormControl-marginNormal': {
        margin: '0px',
    }
});

export default function FeedbackPopup({
    show = false,
    closePopup = (state) => { },
}) {
    const {
        feedBackFinding, 
        setFeedbackFinding, 
        setFeedbackStatus, 
        setVisiblePreviews,
        selectedClassifer,
        setSelectedClassifier,
        classifierIdx,
        setClassifierIdx,
        setClassiferTotal,
        setClassifierTags,
        accessToken,
        zoom,
        showRedactions,
        refreshEndDate,
        totalViolations,
        setTotalViolations,
        onExpand,
        onClose,
        setShowFBConfirm,
        pickFinding,
        scrollIndex,
        isSharingClassifier,
        validFindingFromRedaction,
        sharingClassifierRef,
        isViewFullDocumentClicked,
        fullDocumentPreviewRef,
        subscriptionId
    } = useContext(SideDetailContext);

    const customClasses = useDetectionStyles();
    const classes = useStyles();
    const { api } = useAppToolchain();
    const [feedback, setFeedback] = useState(feedbackOptions[0].value);
    const [additionalInfo, setAdditionalInfo] = useState('');
    const { auth } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const handleRadioChange = (event) => {
        setFeedback(event.target.value);
        setAdditionalInfo('');
        setError(false);
    };

    const handleAdditionalInfoChange = (event) => {
        setAdditionalInfo(event.target.value);
    };

    const handleBlur = () => {
        const containsOnlySpace = /^\s+$/.test(feedback);
        setAdditionalInfo(additionalInfo.trim());
        if(feedback == "RemoveDetection") return;

        if(additionalInfo.length === 0 || containsOnlySpace) {
            setError(true);
        }else {
            setError(false);
        }
      }

    const submitFalsePositiveFeedback = async () => {
        if (feedback === "KeepDetection" && additionalInfo.trim().length === 0) {
            setError(true);
            return;
        }
        setLoading(true);
    
        const splitUrl = feedBackFinding.previewUrl.split('api');
        const urlWithSnapshotAndPreviewID = splitUrl[splitUrl.length - 1];
        const { id: userId } = auth.user;
        const {
            findingId,
            findingType,
            findingTag: findingClassification,
            topLeftCoordinate,
            bottomRightCoordinate,
            previewIndex,
            previewId
        } = feedBackFinding;
    
        const reqPayload = {
            category: "FalsePositive",
            subCategory: feedback,
            details: additionalInfo,
            userId,
            findingId,
            findingType,
            findingClassification,
            topLeftCoordinate,
            bottomRightCoordinate
        };
    
        try {
            const { feedId, feedResolved } = await api.submitRedactionFeedback(urlWithSnapshotAndPreviewID, reqPayload);
    
            if (!feedId || feedId.length === 0) throw new Error("Feedback submission failed!");

            if(feedback === "RemoveDetection") {
                if ((feedResolved || totalViolations === 1) && validFindingFromRedaction) {
                    handleSingleViolation();
                } else {
                    await handleMultipleViolations(feedId, previewIndex, previewId);
                }
    
                if(validFindingFromRedaction) setTotalViolations(prev => prev - 1);
            }
            setFeedbackStatus('success');
        } catch (err) {
            setFeedbackStatus('error');
            console.error(err.message);
        } finally {
            resetFormState();     
        }
    };
    
    const handleSingleViolation = () => {
        onExpand(false);
        refreshEndDate();
        onClose();
        setAdditionalInfo('');
        setShowFBConfirm(true);
    };
    
    const handleMultipleViolations = async (feedId, previewIndex, previewId) => {
        try {
            const { previews, tags } = await mapWithCurrentPreviews(feedId, previewId, previewIndex);
    
            if (!previews || !tags || previews.length === 0 || tags.length === 0) {
                throw new Error("Feedback submission failed!");
            }
        
            const timeStamp = new Date().getTime();
        
            setVisiblePreviews((prev) => {
                return prev.map((prevItem, i) => {
                    if (i === previewIndex) {
                        return { 
                            ...previews[previewIndex],
                            redactedURL: `${prevItem.url }?access_token=${accessToken}&t=${timeStamp}`,
                            revealedURL: `${prevItem.url }/reveal?access_token=${accessToken}&t=${timeStamp}`
                        }
                    } else {
                        return prevItem;
                    }
                });
            });
        
            setClassifierTags(tags.filter(tag => !isSharingClassifier(tag.text)));
        
            const selectedClassiferIndex = tags.findIndex(item => item.text === selectedClassifer);
            checkNextFinding(selectedClassiferIndex, tags, previews);
            preloadImage(previewIndex, previews, timeStamp, showRedactions);
        }catch (err) {
            throw new Error("Feedback submission failed!");
        }
        
    };

    const mapWithCurrentPreviews = async(feedId, previewId, previewIndex) => {
        try {
            const { previews, tags } = await api.getFeedV2(feedId, subscriptionId);

            if(!isViewFullDocumentClicked) {
                updateFullDocumentPreview(previews, previewId)
                return { previews: previews.slice(0, -1), tags };
            } 

            const updatedPreview = previews.find(preview => preview.id === previewId);
            fullDocumentPreviewRef.current[previewIndex] = updatedPreview;

            return { previews: [...fullDocumentPreviewRef.current], tags };
        }catch (err) {
            throw new Error("Feedback submission failed!");
        }
    }
    
    const resetFormState = () => {
        closePopup(true);
        setLoading(false);
        setFeedbackFinding({});
        setAdditionalInfo('');
        setFeedback(feedbackOptions[0].value);
    };
    
    const updateFullDocumentPreview = async(previews, previewId) => {
        const updatedPreview = previews.find(preview => preview.id === previewId);
        const previewIndex = fullDocumentPreviewRef.current.findIndex(preview => preview.id === previewId);
        fullDocumentPreviewRef.current[previewIndex] = updatedPreview;
    }
    

    const checkNextFinding = (selectedClassiferIndex, tags, previews) => {
        const shouldCallNextFinding = scrollIndex === feedBackFinding.previewIndex;

        if (selectedClassiferIndex !== -1) {
            const { count: updatedClassifierTotal } = tags[selectedClassiferIndex];
            setClassiferTotal(updatedClassifierTotal);
            let idx = classifierIdx % updatedClassifierTotal;
            if (idx == 0) idx = updatedClassifierTotal;
            setClassifierIdx(idx);
    
            if(shouldCallNextFinding) pickFinding(selectedClassifer, idx, zoom, previews);
            return;
        }
    
        const tag = tags.find(tag => !isSharingClassifier(tag.text));

        if(tag == null) return;

        const { text, count } = tag;
        setSelectedClassifier(text);
        setClassifierIdx(1);
        setClassiferTotal(count);
    
        if(shouldCallNextFinding) pickFinding(text, 1, zoom, previews);
    }
    

    const preloadImage = (idx, previews, timeStamp, currShowRedactions) => {
        const preview = previews[idx];
        const img = new Image();
        img.src = !currShowRedactions ? 
                    `${preview.url}?access_token=${accessToken}&t=${timeStamp}` : 
                    `${preview.url}/reveal?access_token=${accessToken}&t=${timeStamp}`;
	};

    return (
        <BDSProvider>
            <Dialog open={show}>
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <img
                            src={cudaLogo}
                            alt="Barracuda Data Inspector"
                            height={'36px'}
                        />

                        <IconButton
                            sx={{marginLeft: 'auto'}}
                            aria-label="Close"
                            onClick={() => {
                                setFeedbackFinding({});
                                setAdditionalInfo('');
                                closePopup(false);
                                setFeedback(feedbackOptions[0].value);
                            }}
                            edge="end"
                            size="small"
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h2" className={customClasses['fb-h2']}>
                        Detection feedback
                    </Typography>
                    <Typography variant="body1">
                    Select the reason for removing the detection. Your feedback helps improve the accuracy of our data classification.
                    </Typography>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="female"
                        name="radio-buttons-group"
                        color='primary'
                        value={feedback}
                        onChange={handleRadioChange}
                    >
                        {
                            feedbackOptions.map((option, index) => (
                                <Box key={index}>
                                    <FormControlLabel 
                                        value={option.value} 
                                        control={<Radio style={{ paddingLeft: '10px' }} color='primary' />} 
                                        label={option.label}
                                        
                                    />
                                    {
                                        feedback === option.value && (
                                            <StyledTextField
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                                multiline
                                                minRows={option.value === "KeepDetection" ? 3 : 1}
                                                helperText={option.helperText}
                                                FormHelperTextProps={{
                                                    style: {
                                                        color: error ? '#DE3131' : '#828282',
                                                        fontWeight: error ? 800 : 400,
                                                    },
                                                }}
                                                onChange={handleAdditionalInfoChange}
                                                value={additionalInfo}
                                                size="small"
                                                error={error}
                                                onBlur={handleBlur}
                                            />
                                        )
                                    }
                                </Box>
                                
                            ))
                        }
                    </RadioGroup>
                </DialogContent>

                {
                    totalViolations === 1 && validFindingFromRedaction && sharingClassifierRef.current.length === 0 && 
                        <Alert severity="info">
                            This is the last finding on this document. Successful submission of feedback will resolve this Detection and move it under Resolved tab on the Detections page.
                        </Alert>
                }

                {
                    totalViolations === 1 && validFindingFromRedaction && sharingClassifierRef.current.length > 0 && 
                        <Alert severity="info">
                            This is the last finding on this document. Successful submission of feedback will close the preview.
                        </Alert>
                }

                {loading &&<div className={customClasses['p_10']}> <LoadingIndicator message="Submitting feedback" /></div>}
                {!loading && <DialogActions className={classes['m_1']}>
                    <Button
                        bdsType="interactiveEmphasis"
                        fullWidth
                        disabled={false}
                        onClick={submitFalsePositiveFeedback}
                    >
                        Submit
                    </Button>
                </DialogActions>}
            </Dialog>
        </BDSProvider>
    );
}
